/**
 * @flow
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Input as BaseInput, Icon, Popup } from 'semantic-ui-react';
import { DayPicker, useNavigation, useDayPicker } from 'react-day-picker';
import { format as dateFnsFormat, parse, isValid } from 'date-fns';
import { enUS, ru as ruLocale, lv as lvLocale } from 'date-fns/locale';
import { castArray } from 'lodash';

import 'react-day-picker/dist/style.css';

const localeMap = { en: enUS, ru: ruLocale, lv: lvLocale };
const getDateFnsLocale = locale => localeMap[locale] || enUS;

const formatDate = (date, fmt, locale) =>
    dateFnsFormat(new Date(date), fmt, { locale: getDateFnsLocale(locale) });

const CalendarPopup = styled(Popup)`
    padding: 0 !important;
    z-index: 10001 !important;
`;

function Caption({ displayMonth, displayIndex }) {
    const { goToMonth, nextMonth, previousMonth } = useNavigation();
    const { locale, numberOfMonths } = useDayPicker();
    const isFirst = displayIndex === 0;
    const isLast = displayIndex === numberOfMonths - 1;
    const btnStyle = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em', padding: '8px 12px' };
    const placeholder = <span style={{ display: 'inline-block', width: '43px' }} />;
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px' }}>
            {isFirst
                ? <button style={btnStyle} disabled={!previousMonth} onClick={() => previousMonth && goToMonth(previousMonth)}>{'<'}</button>
                : placeholder}
            <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{dateFnsFormat(displayMonth, 'LLLL yyyy', { locale }).replace(/^\S/, c => c.toUpperCase())}</span>
            {isLast
                ? <button style={btnStyle} disabled={!nextMonth} onClick={() => nextMonth && goToMonth(nextMonth)}>{'>'}</button>
                : placeholder}
        </div>
    );
}

// Prevent customSize from reaching the DOM — styled-components v2 forwards all
// props to the wrapped component, and Semantic UI passes unknowns to <input>.
const FilteredInput = ({ customSize: _cs, ...props }) => <BaseInput {...props} />;

const Input = styled(FilteredInput)`
    ${props => props.customSize && 'width: 240px;'}
    &.ui.icon.input input {
        padding-left: 2.67142857em !important;
        padding-right: 1em !important;
        cursor: pointer;
        text-transform: capitalize;
    }
`;

// v8 DayPicker is a function component — no PropTypes static, so we can style it directly.
// We pass className through explicitly so styled-components can inject the generated class.
const StyledDayPicker = styled(({ className, captionLayout, ...props }) => (
    <DayPicker
        className={className}
        captionLayout={captionLayout}
        components={captionLayout !== 'dropdown' ? { Caption } : undefined}
        {...props}
    />
))`
    & table {
        border-collapse: separate !important;
    }
    & .rdp-cell,
    & .rdp-head_cell {
        border: none !important;
    }
    & .rdp-head_cell {
        text-decoration: none !important;
        font-weight: bold !important;
        text-transform: none !important;
        font-size: inherit !important;
        color: inherit !important;
    }
    & .rdp-day_today:not(.rdp-day_selected) {
        background-color: transparent !important;
        color: #ff4b4b !important;
        font-weight: normal !important;
    }
    & .rdp-day_selected:not(.rdp-day_range_start):not(.rdp-day_range_end):not(.rdp-day_range_middle) {
        background-color: #4a90e2 !important;
        color: white !important;
        border-radius: 50% !important;
    }
    & .rdp-day.rdp-day_selected.rdp-day_range_middle {
        background-color: #f0f8ff !important;
        color: #4a90e2;
    }
    & .rdp-day {
        border-radius: 0 !important;
    }
    & .rdp-day_range_start {
        border-top-left-radius: 50% !important;
        border-bottom-left-radius: 50% !important;
    }
    & .rdp-day_range_end {
        border-top-right-radius: 50% !important;
        border-bottom-right-radius: 50% !important;
    }
    & .rdp-day_workday {
        position: relative;
    }
    & .rdp-day_workday:after {
        position: absolute;
        display: block;
        content: '';
        width: 5px;
        height: 5px;
        background-color: #ff4b4b;
        border-radius: 50%;
        bottom: 2px;
        left: calc(50% - 2.5px);
    }
    & .rdp-day_workday.rdp-day_outside:after {
        display: none;
    }
`;

// Birthday field: plain text input (editable, format dd.MM.yyyy) + floating DayPicker popup.
// Replaces v6 DayPickerInput which hardcoded require('moment').
function TextInputCalendar({ value, onChange, format: fmt, locale }) {
    const toStr = useCallback(
        v => (v ? formatDate(v, fmt, locale) : ''),
        [fmt, locale]
    );

    const [inputValue, setInputValue] = useState(() => toStr(value));
    const [isOpen, setIsOpen] = useState(false);

    // Sync display when external value changes (e.g. form reset).
    useEffect(() => {
        setInputValue(toStr(value));
    }, [value, toStr]);

    const handleChange = useCallback(
        e => {
            const str = e.target.value;
            setInputValue(str);
            const parsed = parse(str, fmt, new Date());
            if (isValid(parsed)) onChange(parsed);
        },
        [fmt, onChange]
    );

    const handleBlur = useCallback(() => {
        if (!isValid(parse(inputValue, fmt, new Date()))) {
            setInputValue('');
            onChange('');
        }
    }, [inputValue, fmt, onChange]);

    const handleDaySelect = useCallback(
        day => {
            if (!day) return;
            setInputValue(formatDate(day, fmt, locale));
            onChange(day);
            setIsOpen(false);
        },
        [fmt, locale, onChange]
    );

    const selected = (() => {
        const p = parse(inputValue, fmt, new Date());
        return isValid(p) ? p : undefined;
    })();

    return (
        <CalendarPopup
            trigger={
                <span>
                    <BaseInput
                        value={inputValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onClick={() => setIsOpen(true)}
                    />
                </span>
            }
            on="click"
            open={isOpen}
            onClose={() => setIsOpen(false)}
            onOpen={() => setIsOpen(true)}
            position="bottom left"
            flowing
        >
            <Popup.Content>
                <StyledDayPicker
                    mode="single"
                    selected={selected}
                    onSelect={handleDaySelect}
                    locale={getDateFnsLocale(locale)}
                    captionLayout="dropdown"
                    fromYear={1900}
                    toYear={new Date().getFullYear()}
                />
            </Popup.Content>
        </CalendarPopup>
    );
}

const Calendar = ({
    rawValue,
    value,
    onChange,
    onBlur,
    format,
    position,
    locale,
    dayPickerProps,
    className,
    firstDayOfWeek,
    dispatch,
    isOwner,
    isOpen,
    openPopup,
    closePopup,
    field,
    ...props
}) => {
    const { isDayPickerInput, ...restDayPickerProps } = dayPickerProps;

    if (isDayPickerInput) {
        return (
            <TextInputCalendar
                value={value}
                onChange={onChange}
                format={format}
                locale={locale}
            />
        );
    }

    return (
        <CalendarPopup
            trigger={
                <span>
                    <Input
                        value={
                            value
                                ? castArray(value)
                                      .map(item => formatDate(item, format, locale))
                                      .join(' - ')
                                : ''
                        }
                        icon={<Icon name="calendar" color="blue" />}
                        iconPosition="left"
                        onBlur={() => {
                            onBlur && onBlur(value);
                        }}
                        {...props}
                    />
                </span>
            }
            on="click"
            open={isOpen}
            onClose={closePopup}
            onOpen={openPopup}
            position={position}
            flowing
        >
            <Popup.Content>
                <StyledDayPicker
                    className={className}
                    locale={getDateFnsLocale(locale)}
                    {...restDayPickerProps}
                    onDayClick={(() => {
                        const custom = restDayPickerProps.onDayClick;
                        return custom
                            ? date => custom(date, closePopup)
                            : date => {
                                  if (onChange) onChange(date);
                                  closePopup();
                              };
                    })()}
                />
            </Popup.Content>
        </CalendarPopup>
    );
};

Calendar.defaultProps = {
    position: 'bottom left',
    dayPickerProps: {},
};

export default Calendar;
